import React from "react";
import { view } from "@orderofchaos/mvvm";
import { Layout } from "@/components/Layout/Layout";
import Button, { ButtonDesign } from "@/components/Button/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog/ConfirmDialog";
import { formatTypeTag, normalizeTypeTags } from "@/utils/typeTags";
import { DeletedEntitiesPageViewModel } from "./DeletedEntitiesPageViewModel";
import styles from "./DeletedEntitiesPage.module.css";

export const DeletedEntitiesPage: React.FC = view(DeletedEntitiesPageViewModel)(
  ({
    viewModel: {
      isAdmin,
      isLoading,
      error,
      deletedProducts,
      deletedBoxes,
      deletedProductsCount,
      deletedBoxesCount,
      totalDeleted,
      isConfirmOpen,
      confirmTitle,
      confirmDescription,
      goBack,
      refresh,
      restoreProduct,
      restoreBox,
      requestHardDeleteProduct,
      requestHardDeleteBox,
      cancelHardDelete,
      confirmHardDelete,
      isProcessing,
      formatDeletedAt,
      safeText,
      getProductAudit,
      getBoxAudit,
      formatAuditActor,
      formatAuditAt,
      formatAuditSource,
    },
  }) => {
    return (
      <Layout title={"Удаленные"}>
        <section className={styles.root}>
          <div className={styles.toolbar}>
            <div className={styles.titleBlock}>
              <strong className={styles.title}>Удаленные сущности</strong>
              <span className={styles.subtitle}>
                Всего ожидают подтверждения: {totalDeleted}
              </span>
            </div>
            <Button
              design={ButtonDesign.ghost}
              className={styles.refresh}
              onClick={() => void refresh()}
              disabled={isLoading}
            >
              обновить
            </Button>
          </div>

          {!isAdmin ? (
            <div className={styles.stateCard}>
              <strong>Недостаточно прав</strong>
              <span>Раздел доступен только администратору</span>
              <Button design={ButtonDesign.basic} onClick={goBack}>
                на главную
              </Button>
            </div>
          ) : (
            <>
              {error && <div className={styles.error}>{error}</div>}

              {isLoading ? (
                <div className={styles.stateCard}>
                  <strong>Загружаем удаленные сущности</strong>
                  <span>Подготавливаем данные для подтверждения</span>
                </div>
              ) : (
                <>
                  <div className={styles.cards}>
                    <article className={styles.card}>
                      <p className={styles.value}>{deletedProductsCount}</p>
                      <p className={styles.label}>товары</p>
                    </article>
                    <article className={styles.card}>
                      <p className={styles.value}>{deletedBoxesCount}</p>
                      <p className={styles.label}>коробки</p>
                    </article>
                  </div>

                  <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <strong className={styles.sectionTitle}>Товары</strong>
                      <span className={styles.sectionCount}>
                        {deletedProductsCount}
                      </span>
                    </div>

                    {deletedProductsCount === 0 ? (
                      <div className={styles.emptyState}>
                        Удаленных товаров нет
                      </div>
                    ) : (
                      <ul className={styles.list}>
                        {deletedProducts.map((item) => {
                          const processing = isProcessing("product", item.id);
                          const audit = getProductAudit(item.id);
                          const typeTags = normalizeTypeTags(item.typeTags);

                          return (
                            <li key={item.id} className={styles.item}>
                              <article className={styles.entityCard}>
                                <div className={styles.entityHeader}>
                                  <strong className={styles.entityName}>
                                    {item.name}
                                  </strong>
                                  <span className={styles.entityDate}>
                                    {formatDeletedAt(item.deletedAt)}
                                  </span>
                                </div>
                                <p className={styles.entityText}>
                                  причина: {safeText(item.deleteReason)}
                                </p>
                                <p className={styles.entityText}>
                                  удалил: {formatAuditActor(audit)}
                                </p>
                                <p className={styles.entityText}>
                                  зафиксировано: {formatAuditAt(audit)}
                                </p>
                                <p className={styles.entityText}>
                                  источник: {formatAuditSource(audit)}
                                </p>
                                {typeTags.length > 0 && (
                                  <div className={styles.metaRow}>
                                    {typeTags.map((tag) => (
                                      <span
                                        key={`${item.id}-${tag}`}
                                        className={styles.meta}
                                      >
                                        {formatTypeTag(tag)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <div className={styles.actions}>
                                  <Button
                                    design={ButtonDesign.basic}
                                    onClick={() => void restoreProduct(item)}
                                    disabled={processing}
                                    isLoading={processing}
                                  >
                                    восстановить
                                  </Button>
                                  <Button
                                    design={ButtonDesign.basic}
                                    onClick={() =>
                                      requestHardDeleteProduct(item)
                                    }
                                    disabled={processing}
                                  >
                                    уничтожить
                                  </Button>
                                </div>
                              </article>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>

                  <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <strong className={styles.sectionTitle}>Коробки</strong>
                      <span className={styles.sectionCount}>
                        {deletedBoxesCount}
                      </span>
                    </div>

                    {deletedBoxesCount === 0 ? (
                      <div className={styles.emptyState}>
                        Удаленных коробок нет
                      </div>
                    ) : (
                      <ul className={styles.list}>
                        {deletedBoxes.map((item) => {
                          const processing = isProcessing("box", item.id);
                          const audit = getBoxAudit(item.id);
                          const typeTags = normalizeTypeTags(item.typeTags);

                          return (
                            <li key={item.id} className={styles.item}>
                              <article className={styles.entityCard}>
                                <div className={styles.entityHeader}>
                                  <strong className={styles.entityName}>
                                    {item.name}
                                  </strong>
                                  <span className={styles.entityDate}>
                                    {formatDeletedAt(item.deletedAt)}
                                  </span>
                                </div>
                                <p className={styles.entityText}>
                                  причина: {safeText(item.deleteReason)}
                                </p>
                                <p className={styles.entityText}>
                                  удалил: {formatAuditActor(audit)}
                                </p>
                                <p className={styles.entityText}>
                                  зафиксировано: {formatAuditAt(audit)}
                                </p>
                                <p className={styles.entityText}>
                                  источник: {formatAuditSource(audit)}
                                </p>
                                {typeTags.length > 0 && (
                                  <div className={styles.metaRow}>
                                    {typeTags.map((tag) => (
                                      <span
                                        key={`${item.id}-${tag}`}
                                        className={styles.meta}
                                      >
                                        {formatTypeTag(tag)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <div className={styles.actions}>
                                  <Button
                                    design={ButtonDesign.basic}
                                    onClick={() => void restoreBox(item)}
                                    disabled={processing}
                                    isLoading={processing}
                                  >
                                    восстановить
                                  </Button>
                                  <Button
                                    design={ButtonDesign.basic}
                                    onClick={() => requestHardDeleteBox(item)}
                                    disabled={processing}
                                  >
                                    уничтожить
                                  </Button>
                                </div>
                              </article>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                </>
              )}
            </>
          )}
        </section>

        <ConfirmDialog
          isOpen={isConfirmOpen}
          title={confirmTitle}
          description={confirmDescription}
          tone={"danger"}
          confirmLabel={"уничтожить"}
          cancelLabel={"отмена"}
          onCancel={cancelHardDelete}
          onConfirm={() => void confirmHardDelete()}
        />
      </Layout>
    );
  },
);
